import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const location = useLocation();

  const primaryLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Algo', path: '/algorithm' },
    { name: 'Loans & JV', path: '/loans' },
    { name: 'Contact', path: '/contact' },
  ];

  const moreLinks = [
    { name: 'Franchise', path: '/franchise' },
    { name: 'Partner', path: '/partner' },
    { name: 'Gallery', path: '/gallery' },
  ];

  const allLinks = [...primaryLinks, ...moreLinks];

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-more-menu]')) {
        setIsMoreOpen(false);
      }
    };
    if (isMoreOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMoreOpen]);

  const isActive = (path) => location.pathname === path;
  const isMoreActive = moreLinks.some((link) => link.path === location.pathname);

  const linkClass = (path) =>
    `transition-all hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm ${
      isActive(path)
        ? 'text-brand-gold drop-shadow-md border-b-2 border-brand-gold pb-1'
        : 'hover:text-brand-gold hover:drop-shadow-md'
    }`;

  return (
    <header className="sticky top-0 z-50 w-full max-w-full bg-brand-primary/90 backdrop-blur-xl border-b border-white/20 shadow-lg transition-all">
      <div className="px-4 py-4 lg:px-8 flex justify-between items-center max-w-[1400px] mx-auto">
        <Link
          to="/"
          className="flex items-center space-x-3 shrink-0 max-w-[75%] sm:max-w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-lg"
        >
          <div
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-tr from-brand-secondary via-brand-gold to-brand-cream shadow-[0_0_15px_rgba(255,195,0,0.4)] border border-brand-cream/50 shrink-0"
            aria-hidden="true"
          />
          <span className="text-[15px] sm:text-lg lg:text-xl font-extrabold tracking-wider text-brand-cream drop-shadow-sm truncate">
            ECO WORLD CAPITAL
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 ml-auto text-base font-bold tracking-wide text-brand-cream" aria-label="Main navigation">
          {primaryLinks.map((link) => (
            <Link key={link.path} to={link.path} className={linkClass(link.path)}>
              {link.name}
            </Link>
          ))}
          <div className="relative" data-more-menu>
            <button
              type="button"
              onClick={() => setIsMoreOpen(!isMoreOpen)}
              aria-expanded={isMoreOpen}
              aria-haspopup="true"
              className={`inline-flex items-center gap-1 transition-all hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-sm ${
                isMoreActive ? 'text-brand-gold' : 'hover:text-brand-gold'
              }`}
            >
              More
              <ChevronDown size={16} className={`transition-transform ${isMoreOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            {isMoreOpen && (
              <div className="absolute top-full right-0 mt-2 min-w-[160px] bg-brand-primary-dark border border-white/20 rounded-xl shadow-2xl py-2 z-50">
                {moreLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block px-4 py-2.5 text-sm font-bold transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:bg-white/10 ${
                      isActive(link.path) ? 'text-brand-gold' : 'text-brand-cream'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden ml-auto text-brand-cream hover:text-brand-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold p-2 shrink-0 rounded-lg"
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
        >
          <svg className="w-7 h-7 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            {isMobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      <div
        className={`lg:hidden absolute top-full left-0 w-full bg-brand-primary-dark border-b border-white/20 shadow-2xl transition-all duration-300 ease-in-out overflow-hidden ${
          isMobileMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="flex flex-col px-6 py-8 space-y-5 text-center overflow-y-auto" aria-label="Mobile navigation">
          {allLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`font-extrabold text-lg tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold rounded-lg py-1 ${
                isActive(link.path) ? 'text-brand-gold' : 'text-brand-cream hover:text-brand-gold'
              }`}
            >
              {link.name}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  );
};

export default Header;
